import { useQuery } from '@tanstack/react-query'

import { get } from '@/lib/api'

import type { IssuePriority, IssueStatus } from './use-issues'

// ==================== Types ====================

export interface IssueWithWaitingTime {
  id: string
  code: string
  title: string
  status: IssueStatus
  priority: IssuePriority
  projectId: string
  assigneeId: string | null
  dueDate: string | null
  createdAt: string
  updatedAt: string | null
  waitingTimeMs: number
}

export interface StageTaskGroup {
  tasks: IssueWithWaitingTime[]
  count: number
}

export interface StageTasksResult {
  development: StageTaskGroup
  codeReview: StageTaskGroup
  testing: StageTaskGroup
  total: number
}

export interface MyStageTasksFilters {
  projectId?: string
}

// ==================== Query Keys ====================

export const myStageTasksKeys = {
  all: ['my-stage-tasks'] as const,
  lists: () => [...myStageTasksKeys.all, 'list'] as const,
  list: (filters: MyStageTasksFilters) => [...myStageTasksKeys.lists(), filters] as const,
}

// ==================== Hooks ====================

/**
 * 获取我的阶段任务
 * Requirements: 7.1
 * Note: Uses /pm/workflow/my-stage-tasks to avoid route conflict
 */
export function useMyStagesTasks(filters: MyStageTasksFilters = {}) {
  return useQuery({
    queryKey: myStageTasksKeys.list(filters),
    queryFn: () => get<StageTasksResult>('/pm/workflow/my-stage-tasks', filters as Record<string, unknown>),
  })
}

/**
 * 格式化等待时间
 * @param waitingTimeMs 等待时间（毫秒）
 * @returns 格式化后的字符串
 */
export function formatStageWaitingTime(waitingTimeMs: number): string {
  const hours = Math.floor(waitingTimeMs / (1000 * 60 * 60))
  const days = Math.floor(hours / 24)
  
  if (days > 0) {
    const remainingHours = hours % 24
    if (remainingHours > 0) {
      return `${days}天${remainingHours}小时`
    }
    return `${days}天`
  }
  
  if (hours > 0) {
    return `${hours}小时`
  }
  
  const minutes = Math.floor(waitingTimeMs / (1000 * 60))
  if (minutes > 0) {
    return `${minutes}分钟`
  }
  
  return '刚刚'
}

/**
 * 判断任务是否等待过长
 * @param waitingTimeMs 等待时间（毫秒）
 * @param thresholdHours 阈值（小时），默认24小时
 * @returns 是否超过阈值
 */
export function isLongWaiting(waitingTimeMs: number, thresholdHours: number = 24): boolean {
  const thresholdMs = thresholdHours * 60 * 60 * 1000
  return waitingTimeMs > thresholdMs
}
